import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button, FormControlLabel, IconButton } from "@mui/material";
import { useFormik } from "formik";
// import * as yup from "yup";
import {
  CustomCheckbox,
  CustomDatePicker,
  Input,
  MuiBox,
  MuiTypography,
  Select,
} from "components/atoms";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Close from "assets/images/svg-icons/closeIcon.svg";
import {
  PeopleSuggestionAction,
  SearchFilesFolderAction,
} from "store/middlewares/general";
import { resetFilterValues, setFilterValues } from "store/slices/general";
import dayjs from "dayjs";

// // Optional: validation schema
// const validationSchema = yup.object({
//   // Add any validation rules if needed
// });

const FilterMenu = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const personRef = useRef(null);
  const location = useLocation();
  const isAlreadyOnSearch = location.pathname === "/drive/search";
  const { filterValues, peopleSuggestion } = useSelector(
    (state) => state.general,
  );

  const originPath = location.state?.originPath || "/drive";

  // Prepare initial values for formik: convert stored timestamps to dayjs objects
  const initialValues = {
    ...filterValues,
    after: filterValues?.after ? dayjs(filterValues.after) : null,
    before: filterValues?.before ? dayjs(filterValues.before) : null,
    sharedTo: filterValues?.sharedTo || "",
  };

  // Helper: convert dayjs object to Unix timestamp (milliseconds)
  const getUnixTimestamp = (dayjsObj) => {
    return dayjsObj ? dayjsObj.valueOf() : null;
  };

  // Helper: get date range based on selected dateModified option
  const getDateRangeForModified = (range, after, before) => {
    const today = dayjs();
    let start = null,
      end = null;

    switch (range) {
      case "today":
        start = today.startOf("day");
        end = today.endOf("day");
        break;
      case "yesterday":
        start = today.subtract(1, "day").startOf("day");
        end = today.subtract(1, "day").endOf("day");
        break;
      case "lst7":
        start = today.subtract(7, "days").startOf("day");
        end = today.endOf("day");
        break;
      case "lst30":
        start = today.subtract(30, "days").startOf("day");
        end = today.endOf("day");
        break;
      case "lst90":
        start = today.subtract(90, "days").startOf("day");
        end = today.endOf("day");
        break;
      case "custom":
        start = after;
        end = before;
        break;
      case "anytime":
      default:
        start = null;
        end = null;
        break;
    }

    return { start, end };
  };

  const formik = useFormik({
    initialValues,
    // validationSchema,
    onSubmit: (values) => {
      setTimeout(() => {
        let startTimestamp = null,
          endTimestamp = null;
        if (values.dateModified !== "anytime") {
          const { start, end } = getDateRangeForModified(
            values.dateModified,
            values.after,
            values.before,
          );
          startTimestamp = getUnixTimestamp(start);
          endTimestamp = getUnixTimestamp(end);
        }

        const valuesToStore = {
          ...values,
          after: getUnixTimestamp(values.after),
          before: getUnixTimestamp(values.before),
        };
        dispatch(setFilterValues(valuesToStore));

        const apiPayload = {
          type: values.type,
          ownerType: values.ownerType,
          owner: values.owner,
          location: values.location,
          after: startTimestamp,
          before: endTimestamp,
          // followUp: values.followUp,
          hasWords: values.hasWords,
          itemName: values.itemName,
          sharedTo: values.sharedTo,
          isTrashed: values.isTrashed,
          isStarred: values.starred,
          isEncrypted: values.isEncrypted,
          awaitingApproval: values.awaitingApproval,
          requestedByMe: values.requestedByMe,
          limit: 50,
        };

        dispatch(SearchFilesFolderAction(apiPayload));
        navigate(`/drive/search`, {
          // replace: true,
          replace: isAlreadyOnSearch,

          state: {
            ts: Date.now(),
            // Keep original origin, don't overwrite with /drive/search
            originPath: location.state?.originPath || location.pathname,
          },
        });
        onClose(); // move here after navigate
      }, 50);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (location.pathname !== "/drive/search") {
      dispatch(resetFilterValues());
      setPersonDisplayName("");
      setShowDropdown(false);
    }
  }, [location.pathname, dispatch]);
  // Options for dropdowns
  const typeOptions = [
    { label: t("modal.filter.any"), value: "any" },
    { label: t("common.folderCap"), value: "folder" },
    { label: t("common.file"), value: "file" },
  ];

  const ownerOptions = [
    { label: t("modal.filter.anyone"), value: "anyone" },
    { label: t("modal.filter.ownedByMe"), value: "owned_by_me" },
    { label: t("modal.filter.notOwnedByMe"), value: "not_owned_by_me" },
    { label: t("modal.filter.specificPerson"), value: "specific_person" },
  ];

  const locationOptions = [
    { label: t("modal.filter.anywhere"), value: "anywhere" },
    { label: t(t("common.myDrive")), value: "my_drive" },
    { label: t("common.sharedWithMe"), value: "shared_with_me" },
    { label: t("modal.filter.moreLocation"), value: "any" },
  ];

  const dateOptions = [
    { label: t("modal.filter.anytime"), value: "anytime" },
    { label: t("common.today"), value: "today" },
    { label: t("common.yesterday"), value: "yesterday" },
    { label: t("modal.filter.last7Days"), value: "lst7" },
    { label: t("modal.filter.last30Days"), value: "lst30" },
    { label: t("modal.filter.last90Days"), value: "lst90" },
    { label: t("modal.filter.custom"), value: "custom" },
  ];

  // const followupOptions = [
  //   // { label: "-", value: "-" },
  //   { label: t("any"), value: "any" },
  //   { label: t("suggestionsOnly"), value: "suggestions_only" },
  //   { label: t("commentsAssignedToMeOnly"), value: "comments" },
  // ];

  useEffect(() => {
    if (formik.values.ownerType === "specific_person") {
      dispatch(PeopleSuggestionAction());
    }
  }, [formik.values.ownerType]);

  const [showDropdown, setShowDropdown] = useState(false);
  const [personDisplayName, setPersonDisplayName] = useState("");

  useEffect(() => {
    const storedEmail = formik.values.specificPersonName;
    if (peopleSuggestion?.length && storedEmail && !personDisplayName) {
      const matched = peopleSuggestion.find(
        (item) => item?.email === storedEmail,
      );
      if (matched) {
        const fullName =
          `${matched?.first_name ?? ""} ${matched?.last_name ?? ""}`.trim();
        setPersonDisplayName(fullName);
      }
    }
  }, [peopleSuggestion]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (personRef.current && !personRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasAppliedFilters = Object.keys(filterValues || {}).length > 0;
  const canReset = formik.dirty || hasAppliedFilters;

  return (
    <form onSubmit={formik.handleSubmit}>
      <MuiBox
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <MuiBox
          sx={{
            p: "16px",
            backgroundColor: "background.white",
            position: "sticky",
            top: 0,
            zIndex: 9,
            display: "flex",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "background.gray500",
          }}
        >
          <MuiTypography fontWeight="600" variant="20" color="text.darkBlack">
            {t("header.filter")}
          </MuiTypography>
          <IconButton
            sx={{ p: "5px", display: { xs: "flex", sm: "none" } }}
            onClick={onClose}
          >
            <img src={Close} alt="closeIcon" />
          </IconButton>
        </MuiBox>
        <MuiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            p: "16px",
            backgroundColor: "background.white",
          }}
        >
          <Select
            placeholder={t("modal.filter.selectHere")}
            isBorderColor={true}
            label={t("common.type")}
            options={typeOptions}
            value={formik.values.type}
            onChange={(val) => formik.setFieldValue("type", val)}
          />
          <MuiBox
            sx={{
              display: "flex",
              gap: "16px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Select
              placeholder={t("modal.filter.selectHere")}
              isBorderColor={true}
              label={t("modal.filter.owner")}
              options={ownerOptions}
              value={formik.values.ownerType}
              onChange={(val) => formik.setFieldValue("ownerType", val)}
            />
            {formik.values.ownerType === "specific_person" && (
              <MuiBox
                sx={{
                  position: "relative",
                  flex: 1,
                  minWidth: "250px",
                }}
              >
                <Input
                  isBorderColor={true}
                  placeholder={t("modal.filter.enterNameOrEmail")}
                  label={t("modal.filter.name")}
                  value={personDisplayName}
                  // onFocus={() => setShowDropdown(true)}
                  onChange={(e) => {
                    setPersonDisplayName(e.target.value);
                    formik.setFieldValue("owner", "");
                    setShowDropdown(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowDropdown(false);
                    }, 300);
                  }}
                />

                {showDropdown && (
                  <MuiBox
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: "100%",
                      backgroundColor: "#fff",
                      border: "1px solid #dcdcdc",
                      borderRadius: "8px",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                      mt: "6px",
                      height: "max-content",
                      maxHeight: "220px",
                      overflowY: "auto",
                      zIndex: 999,
                    }}
                    className="no-scrollbar"
                  >
                    {peopleSuggestion
                      ?.filter((item) => {
                        const fullName =
                          (item?.first_name ?? "") +
                          " " +
                          (item?.last_name ?? "");
                        // Filter by what the user has typed (display text),
                        // but only show suggestions when no id is selected yet
                        return fullName
                          .toLowerCase()
                          .includes(personDisplayName.toLowerCase());
                      })
                      ?.map((item, index) => {
                        const fullName =
                          (item?.first_name ?? "") +
                          " " +
                          (item?.last_name ?? "");
                        const avatar = item?.image_url;
                        const email = item?.email;
                        return (
                          <MuiBox
                            key={index}
                            onClick={() => {
                              setPersonDisplayName(fullName);
                              formik.setFieldValue("owner", item?.email);
                              setShowDropdown(false);
                            }}
                            sx={{
                              padding: "12px 16px",
                              cursor: "pointer",
                              transition: "0.2s",
                              color: "text.black",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <MuiBox
                              sx={{
                                display: "flex",
                                gap: "8px",
                              }}
                            >
                              <Avatar
                                src={avatar}
                                alt={fullName}
                                className="img-full"
                              />
                              <MuiBox
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  overflow: "hidden",
                                }}
                              >
                                <MuiTypography
                                  fontWeight="500"
                                  variant="16"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {fullName}
                                </MuiTypography>
                                <MuiTypography
                                  fontWeight="400"
                                  variant="12"
                                  color="text.gray"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {email}
                                </MuiTypography>
                              </MuiBox>
                            </MuiBox>
                          </MuiBox>
                        );
                      })}
                  </MuiBox>
                )}
              </MuiBox>
            )}
          </MuiBox>
          {/* <Input
            isBorderColor={true}
            label={t("hasTheWords")}
            placeholder={t("enterWordsFoundInTheFile")}
            value={formik.values.hasWords}
            onChange={(e) => formik.setFieldValue("hasWords", e.target.value)}
          /> */}
          <Input
            isBorderColor={true}
            label={t("modal.filter.itemName")}
            placeholder={t("modal.filter.termThatMatchespartOfFileName")}
            value={formik.values.itemName}
            onChange={(e) => formik.setFieldValue("itemName", e.target.value)}
          />
          <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Select
              placeholder={t("modal.filter.selectHere")}
              isBorderColor={true}
              label={t("common.location")}
              options={locationOptions}
              value={formik.values.location}
              onChange={(val) => formik.setFieldValue("location", val)}
            />

            {/* Show the checkboxes only when location is 'more location' */}
            {formik.values.location === "any" && (
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: { xs: "start", md: "center" },
                  gap: { xs: "2px", sm: "16px" },
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: "auto" }}
                  control={
                    <CustomCheckbox
                      checked={formik.values.isTrashed}
                      disabled={formik.values.starred}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        formik.setFieldValue("isTrashed", checked);
                        if (checked) {
                          formik.setFieldValue("starred", false);
                        }
                      }}
                    />
                  }
                  label={
                    <MuiTypography
                      fontWeight="400"
                      variant="14"
                      sx={{
                        color:
                          formik.values.starred
                            ? "text.gray"
                            : "text.darkBlack",
                        opacity: formik.values.starred ? 0.6 : 1,
                      }}
                    >
                      {t("modal.filter.inTrash")}
                    </MuiTypography>
                  }
                />
                <FormControlLabel
                  sx={{ m: 0, width: "auto" }}
                  control={
                    <CustomCheckbox
                      checked={formik.values.starred}
                      disabled={formik.values.isTrashed}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        formik.setFieldValue("starred", checked);
                        if (checked) {
                          formik.setFieldValue("isTrashed", false);
                        }
                      }}
                    />
                  }
                  label={
                    <MuiTypography
                      fontWeight="400"
                      variant="14"
                      sx={{
                        color:
                          formik.values.isTrashed
                            ? "text.gray"
                            : "text.darkBlack",
                        opacity: formik.values.isTrashed ? 0.6 : 1,
                      }}
                    >
                      {t("common.starred")}
                    </MuiTypography>
                  }
                />
                {/* <FormControlLabel
                  sx={{ m: 0, width: "auto" }}
                  control={
                    <CustomCheckbox
                      checked={formik.values.isEncrypted}
                      onChange={(e) =>
                        formik.setFieldValue("isEncrypted", e.target.checked)
                      }
                    />
                  }
                  label={
                    <MuiTypography
                      fontWeight="400"
                      variant="14"
                      color="text.darkBlack"
                    >
                      {t("modal.filter.encrypted")}
                    </MuiTypography>
                  }
                /> */}
              </MuiBox>
            )}
          </MuiBox>
          <Select
            placeholder={t("modal.filter.selectHere")}
            isBorderColor={true}
            label={t("common.dateModified")}
            options={dateOptions}
            value={formik.values.dateModified}
            onChange={(val) => formik.setFieldValue("dateModified", val)}
          />
          {formik.values.dateModified === "custom" && (
            <MuiBox sx={{ display: "flex", gap: "16px" }}>
              <CustomDatePicker
                isBorderColor={true}
                label={t("common.before")}
                value={formik.values.before}
                onChange={(date) => formik.setFieldValue("before", date)}
              />
              <CustomDatePicker
                isBorderColor={true}
                label={t("common.after")}
                value={formik.values.after}
                onChange={(date) => formik.setFieldValue("after", date)}
              />
            </MuiBox>
          )}
          <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <MuiTypography fontWeight="400" variant="16" color="text.darkBlack">
              {t("modal.filter.approvalsAndEsignatures")}
            </MuiTypography>
            <MuiBox
              sx={{
                display: "flex",
                alignItems: {
                  xs: "start",
                  md: "center",
                },
                gap: { xs: "2px", sm: "16px" },
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
              }}
            >
              <FormControlLabel
                sx={{ m: 0, width: "auto" }}
                control={
                  <CustomCheckbox
                    checked={formik.values.awaitingApproval}
                    onChange={(e) =>
                      formik.setFieldValue("awaitingApproval", e.target.checked)
                    }
                  />
                }
                label={
                  <MuiTypography
                    fontWeight="400"
                    variant="14"
                    color="text.darkBlack"
                  >
                    {t("modal.filter.awaitingMyApproval")}
                  </MuiTypography>
                }
              />
              <FormControlLabel
                sx={{ m: 0, width: "auto" }}
                control={
                  <CustomCheckbox
                    checked={formik.values.requestedByMe}
                    onChange={(e) =>
                      formik.setFieldValue("requestedByMe", e.target.checked)
                    }
                  />
                }
                label={
                  <MuiTypography
                    fontWeight="400"
                    variant="14"
                    color="text.darkBlack"
                  >
                    {t("modal.filter.requestedByMe")}
                  </MuiTypography>
                }
              />
            </MuiBox>
          </MuiBox>
          <Input
            isBorderColor={true}
            label={t("modal.filter.sharedTo")}
            placeholder={t("modal.filter.enterNameOrEmail")}
            value={formik.values.sharedTo}
            onChange={(e) => formik.setFieldValue("sharedTo", e.target.value)}
          />
          {/* <Select
            placeholder={t("modal.filter.selectHere")}
            isBorderColor={true}
            label={t("followUps")}
            options={followupOptions}
            value={formik.values.followUp}
            onChange={(val) => formik.setFieldValue("followUp", val)}
          /> */}
        </MuiBox>
      </MuiBox>
      <MuiBox
        sx={{
          display: "flex",
          gap: "12px",
          justifyContent: "end",
          backgroundColor: "background.gray200",
          p: "16px 24px",
          position: "sticky",
          bottom: 0,
        }}
      >
        <Button
          variant="whiteContained"
          type="button"
          onClick={() => {
            onClose();
            setTimeout(() => {
              dispatch(resetFilterValues());
              if (isAlreadyOnSearch) {
                navigate(originPath);
              } // use saved origin instead of navigate(-1)
            }, 50);
          }}
          sx={{
            borderColor: canReset ? "background.blue600" : "transparent",
          }}
          disabled={!canReset}
        >
          {t("modal.filter.reset")}
        </Button>
        <Button variant="blueContained" disabled={!formik.dirty} type="submit">
          {t("commonButton.search")}
        </Button>
      </MuiBox>
    </form>
  );
};

export default FilterMenu;
